import { compact, uniqBy } from 'lodash';

import { isDocumentProjectType, isLongFormProjectType, type ProjectType } from 'src/data/project';
import { doesTemplateOutputIncludeImages, type TemplateOutputType } from 'src/data/template';

import { GENERATION_TOOL, type GenerationTool } from './const';
import {
  getToolDescription,
  getToolMixtureSubGenerationTools,
  getToolTitle,
  isMixtureTool,
} from './toolProps';
import { isBlankLongFormGenerationTool, isFreestyleGenerationTool } from './utils';

interface SectionToolOption {
  tool?: GenerationTool;
  subheader?: string;
}

const IMAGE_OPTIONS: SectionToolOption[] = [
  { tool: GENERATION_TOOL.imageByStyle },
  { tool: GENERATION_TOOL.facebookPostImage },
  { tool: GENERATION_TOOL.linkedinPostImage },
  { tool: GENERATION_TOOL.instagramPostImage },
  { tool: GENERATION_TOOL.pinterestPinImage },
  { tool: GENERATION_TOOL.xPostImage },
  { tool: GENERATION_TOOL.youtubeVideoImage },
  { tool: GENERATION_TOOL.metaAdImage },
  { tool: GENERATION_TOOL.linkedinSingleImageAdImage },
  { tool: GENERATION_TOOL.tweeterAdImage },
  { tool: GENERATION_TOOL.pinterestAdImage },
  { tool: GENERATION_TOOL.googleShoppingAdImage },
  { tool: GENERATION_TOOL.googleAppAdImage },
  { tool: GENERATION_TOOL.outbrainAdImage },
  { tool: GENERATION_TOOL.taboolaAdImage },
  { tool: GENERATION_TOOL.emailImage },
];

const OPTIONS: SectionToolOption[] = [
  { subheader: 'Webpage' },
  { tool: GENERATION_TOOL.landingHeadline },
  { tool: GENERATION_TOOL.landingSubheader },
  { tool: GENERATION_TOOL.landingSectionHeadline },
  { tool: GENERATION_TOOL.landingHeader },
  { tool: GENERATION_TOOL.landingCTA },
  { tool: GENERATION_TOOL.landingBenefits },
  { tool: GENERATION_TOOL.webpageGeneralText },
  { tool: GENERATION_TOOL.landingFeatures },
  { subheader: 'Website' },
  { tool: GENERATION_TOOL.landingSEOMetaTitle },
  { tool: GENERATION_TOOL.landingSEOMetadata },
  { subheader: 'Blog' },
  { tool: GENERATION_TOOL.articleBlogHeadline },
  { tool: GENERATION_TOOL.blogPostSectionSubHeadline },
  { tool: GENERATION_TOOL.blogPostSectionHeadline },
  { tool: GENERATION_TOOL.blogPostIntroParagraph },
  { tool: GENERATION_TOOL.blogPostGeneralText },
  { subheader: 'Email' },
  { tool: GENERATION_TOOL.emailSubject },
  { tool: GENERATION_TOOL.emailGeneralText },
  { tool: GENERATION_TOOL.emailHeadline },
  { tool: GENERATION_TOOL.emailSectionHeadline },
  { tool: GENERATION_TOOL.emailHeader },
  { tool: GENERATION_TOOL.emailIntroParagraph },
  { tool: GENERATION_TOOL.emailBody },
  { tool: GENERATION_TOOL.emailCta },
  { tool: GENERATION_TOOL.emailMixture },
  { subheader: 'Social' },
  { tool: GENERATION_TOOL.facebookPost },
  { tool: GENERATION_TOOL.linkedinPost },
  { tool: GENERATION_TOOL.instagramCaption },
  { tool: GENERATION_TOOL.pinterestPostTitle },
  { tool: GENERATION_TOOL.pinterestPostDescription },
  { tool: GENERATION_TOOL.tweet },
  { tool: GENERATION_TOOL.youtubeTitle },
  { tool: GENERATION_TOOL.youtubeDescription },
  { subheader: 'Ads' },
  { tool: GENERATION_TOOL.facebookPrimaryTextProduct },
  { tool: GENERATION_TOOL.facebookHeadlineProduct },
  { tool: GENERATION_TOOL.linkedinHeadlineSingleImage },
  { tool: GENERATION_TOOL.linkedinIntroSingleImage },
  { tool: GENERATION_TOOL.linkedinHeadlineProduct },
  { tool: GENERATION_TOOL.linkedinIntroProduct },
  { tool: GENERATION_TOOL.linkedinMessageAdSubject },
  { tool: GENERATION_TOOL.linkedinMessageAdMessage },
  { tool: GENERATION_TOOL.linkedinMessageAdCta },
  { tool: GENERATION_TOOL.linkedinConversationalAdOpeningMessage },
  { tool: GENERATION_TOOL.linkedinConversationalAdPrimaryCtaButtonText },
  { tool: GENERATION_TOOL.linkedinConversationalAdFollowUpMessage },
  { tool: GENERATION_TOOL.linkedinLeadGenFormAdOfferHeadline },
  { tool: GENERATION_TOOL.linkedinLeadGenFormAdOfferDetails },
  { tool: GENERATION_TOOL.linkedinVideoAdIntro },
  { tool: GENERATION_TOOL.linkedinVideoAdHeadline },
  { tool: GENERATION_TOOL.linkedinDocumentAdIntro },
  { tool: GENERATION_TOOL.linkedinDocumentAdHeadline },
  { tool: GENERATION_TOOL.twitterCopyProduct },
  { tool: GENERATION_TOOL.twitterTitleProduct },
  { tool: GENERATION_TOOL.pinterestTitleProduct },
  { tool: GENERATION_TOOL.pinterestDescriptionProduct },
  { tool: GENERATION_TOOL.googleHeadlineProduct },
  { tool: GENERATION_TOOL.googleLongHeadlineProduct },
  { tool: GENERATION_TOOL.googleDescriptionProduct },
  { tool: GENERATION_TOOL.googleSearchAdHeadlineProduct },
  { tool: GENERATION_TOOL.googleSearchAdDescriptionProduct },
  { tool: GENERATION_TOOL.googleMediumHeadlineProduct },
  { tool: GENERATION_TOOL.googleDemandGenAdLongHeadlineProduct },
  { tool: GENERATION_TOOL.googleDemandGenAdDescriptionProduct },
  { tool: GENERATION_TOOL.googleDisplayAdHeadlineProduct },
  { tool: GENERATION_TOOL.googleDisplayAdLongHeadlineProduct },
  { tool: GENERATION_TOOL.googleDisplayAdDescriptionProduct },
  { tool: GENERATION_TOOL.googleShoppingAdTitle },
  { tool: GENERATION_TOOL.googleShoppingAdDescription },
  { tool: GENERATION_TOOL.googleVideoAdHeadlineProduct },
  { tool: GENERATION_TOOL.googleVideoAdLongHeadlineProduct },
  { tool: GENERATION_TOOL.googleVideoAdDescriptionProduct },
  { tool: GENERATION_TOOL.googleAppAdHeadlineProduct },
  { tool: GENERATION_TOOL.googleAppAdDescriptionProduct },
  { tool: GENERATION_TOOL.outbrainHeadlineProduct },
  { tool: GENERATION_TOOL.outbrainDescriptionProduct },
  { tool: GENERATION_TOOL.taboolaHeadlineProduct },
  { tool: GENERATION_TOOL.taboolaDescriptionProduct },
  { subheader: 'Ecommerce' },
  { tool: GENERATION_TOOL.amazonProductListing },
  { tool: GENERATION_TOOL.walmartProductListing },
  { tool: GENERATION_TOOL.generalProductListing },
  { tool: GENERATION_TOOL.basicProductReview },
  { subheader: 'Other' },
  { tool: GENERATION_TOOL.smsPromotional },
];

const SECTION_HEADLINE_TOOLS_TO_EXCLUDE: GenerationTool[] = [
  GENERATION_TOOL.blogPostSectionHeadline,
  GENERATION_TOOL.landingSectionHeadline,
  GENERATION_TOOL.emailSectionHeadline,
];

/**
 * Maps a platform-specific text generation tool to its paired image generation tool.
 * Used to inject the most contextually appropriate image tool into `available_tools`
 * so the algo can suggest it (rather than the generic `imageByStyle`) for image sections.
 */
export const TOOL_TO_SPECIFIC_IMAGE_TOOL: Partial<Record<GenerationTool, GenerationTool>> = {
  // Social
  [GENERATION_TOOL.facebookPost]: GENERATION_TOOL.facebookPostImage,
  [GENERATION_TOOL.linkedinPost]: GENERATION_TOOL.linkedinPostImage,
  [GENERATION_TOOL.instagramCaption]: GENERATION_TOOL.instagramPostImage,
  [GENERATION_TOOL.pinterestPostTitle]: GENERATION_TOOL.pinterestPinImage,
  [GENERATION_TOOL.pinterestPostDescription]: GENERATION_TOOL.pinterestPinImage,
  [GENERATION_TOOL.tweet]: GENERATION_TOOL.xPostImage,
  [GENERATION_TOOL.youtubeTitle]: GENERATION_TOOL.youtubeVideoImage,
  [GENERATION_TOOL.youtubeDescription]: GENERATION_TOOL.youtubeVideoImage,
  // Ads
  [GENERATION_TOOL.facebookPrimaryTextProduct]: GENERATION_TOOL.metaAdImage,
  [GENERATION_TOOL.facebookHeadlineProduct]: GENERATION_TOOL.metaAdImage,
  [GENERATION_TOOL.linkedinHeadlineSingleImage]: GENERATION_TOOL.linkedinSingleImageAdImage,
  [GENERATION_TOOL.linkedinIntroSingleImage]: GENERATION_TOOL.linkedinSingleImageAdImage,
  [GENERATION_TOOL.twitterCopyProduct]: GENERATION_TOOL.tweeterAdImage,
  [GENERATION_TOOL.twitterTitleProduct]: GENERATION_TOOL.tweeterAdImage,
  [GENERATION_TOOL.pinterestTitleProduct]: GENERATION_TOOL.pinterestAdImage,
  [GENERATION_TOOL.pinterestDescriptionProduct]: GENERATION_TOOL.pinterestAdImage,
  [GENERATION_TOOL.googleShoppingAdTitle]: GENERATION_TOOL.googleShoppingAdImage,
  [GENERATION_TOOL.googleShoppingAdDescription]: GENERATION_TOOL.googleShoppingAdImage,
  [GENERATION_TOOL.googleAppAdHeadlineProduct]: GENERATION_TOOL.googleAppAdImage,
  [GENERATION_TOOL.googleAppAdDescriptionProduct]: GENERATION_TOOL.googleAppAdImage,
  [GENERATION_TOOL.outbrainHeadlineProduct]: GENERATION_TOOL.outbrainAdImage,
  [GENERATION_TOOL.outbrainDescriptionProduct]: GENERATION_TOOL.outbrainAdImage,
  [GENERATION_TOOL.taboolaHeadlineProduct]: GENERATION_TOOL.taboolaAdImage,
  [GENERATION_TOOL.taboolaDescriptionProduct]: GENERATION_TOOL.taboolaAdImage,
  // Mixture ads
  [GENERATION_TOOL.facebookMixtureAd]: GENERATION_TOOL.metaAdImage,
  [GENERATION_TOOL.twitterMixtureAd]: GENERATION_TOOL.tweeterAdImage,
  [GENERATION_TOOL.pinterestMixtureAd]: GENERATION_TOOL.pinterestAdImage,
  [GENERATION_TOOL.linkedinMixtureAd]: GENERATION_TOOL.linkedinSingleImageAdImage,
  [GENERATION_TOOL.googleShoppingAd]: GENERATION_TOOL.googleShoppingAdImage,
  [GENERATION_TOOL.googleAppAd]: GENERATION_TOOL.googleAppAdImage,
  [GENERATION_TOOL.outbrainAd]: GENERATION_TOOL.outbrainAdImage,
  [GENERATION_TOOL.taboolaAd]: GENERATION_TOOL.taboolaAdImage,
  [GENERATION_TOOL.emailMixture]: GENERATION_TOOL.emailImage,
  // Email
  [GENERATION_TOOL.emailBody]: GENERATION_TOOL.emailImage,
  [GENERATION_TOOL.emailIntroParagraph]: GENERATION_TOOL.emailImage,
  [GENERATION_TOOL.emailCta]: GENERATION_TOOL.emailImage,
  [GENERATION_TOOL.emailHeadline]: GENERATION_TOOL.emailImage,
  [GENERATION_TOOL.emailHeader]: GENERATION_TOOL.emailImage,
  [GENERATION_TOOL.emailSubject]: GENERATION_TOOL.emailImage,
  [GENERATION_TOOL.emailSectionHeadline]: GENERATION_TOOL.emailImage,
  [GENERATION_TOOL.emailGeneralText]: GENERATION_TOOL.emailImage,
  [GENERATION_TOOL.emailColdOutreachBody]: GENERATION_TOOL.emailImage,
  [GENERATION_TOOL.emailPromotionalBody]: GENERATION_TOOL.emailImage,
};

export const getToolSpecificImageTool = (tool: GenerationTool): GenerationTool | undefined =>
  TOOL_TO_SPECIFIC_IMAGE_TOOL[tool];

const TOOLS_INCOMPATIBLE_WITH_BLANK_TEMPLATE: GenerationTool[] = [
  GENERATION_TOOL.emailColdOutreachBody,
  GENERATION_TOOL.emailPromotionalBody,
  GENERATION_TOOL.emailCta,
  GENERATION_TOOL.emailHeadline,
  GENERATION_TOOL.landingCTA,
];

const TOOLS_TO_REMOVE_FOR_FREESTYLE: GenerationTool[] = [
  GENERATION_TOOL.facebookPrimaryTextProduct,
  GENERATION_TOOL.facebookHeadlineProduct,
  GENERATION_TOOL.linkedinHeadlineSingleImage,
  GENERATION_TOOL.linkedinIntroSingleImage,
  GENERATION_TOOL.twitterCopyProduct,
  GENERATION_TOOL.twitterTitleProduct,
  GENERATION_TOOL.pinterestTitleProduct,
  GENERATION_TOOL.pinterestDescriptionProduct,
  GENERATION_TOOL.landingHeadline,
  GENERATION_TOOL.landingSubheader,
  GENERATION_TOOL.landingCTA,
  GENERATION_TOOL.landingBenefits,
  GENERATION_TOOL.landingFeatures,
  GENERATION_TOOL.landingSEOMetadata,
  GENERATION_TOOL.landingSEOMetaTitle,
  GENERATION_TOOL.landingSectionHeadline,
  GENERATION_TOOL.googleHeadlineProduct,
  GENERATION_TOOL.googleMediumHeadlineProduct,
  GENERATION_TOOL.googleDescriptionProduct,
];

const TOOLS_TO_ADD_FOR_FREESTYLE: GenerationTool[] = [
  GENERATION_TOOL.emailBody,
  GENERATION_TOOL.facebookMixtureAd,
  GENERATION_TOOL.twitterMixtureAd,
  GENERATION_TOOL.pinterestMixtureAd,
  GENERATION_TOOL.linkedinMixtureAd,
  GENERATION_TOOL.googleAppAd,
  GENERATION_TOOL.googleDemandGenAd,
  GENERATION_TOOL.googleDisplayAd,
  GENERATION_TOOL.googleRSA,
];

interface GetSectionToolsOptionsParams {
  projectType?: ProjectType;
  generationTool?: GenerationTool;
  templateOutputType?: TemplateOutputType;
  targetType?: 'infinity' | 'format_popup';
  withFreeStyle?: boolean;
  withoutSubHeaders?: boolean;
  removeSectionHeadlines?: boolean;
  isImageGenerationTool?: boolean;
}

/**
 * Retrieves section tool options based on filtering criteria.
 * @param options - Options for filtering.
 * @param [options.projectType] - project type
 * @param [options.generationTool] - The main generation tool.
 * @param [options.withFreeStyle=false] - Whether to include freestyle writing.
 * @param [options.withoutSubHeaders=false] - Whether to exclude subheaders.
 * @param [options.removeSectionHeadlines=false] - Whether to exclude section headlines.
 * @param [options.isImageGenerationTool=false] - Whether the generation tool is an image generation tool.
 * @returns - Array of filtered generation tool options.
 */
export const getSectionToolsOptions = ({
  projectType,
  generationTool,
  templateOutputType,
  targetType,
  withFreeStyle,
  withoutSubHeaders,
  removeSectionHeadlines,
  isImageGenerationTool,
}: GetSectionToolsOptionsParams): SectionToolOption[] => {
  if (isImageGenerationTool) {
    return IMAGE_OPTIONS;
  }

  let filteredOptions = OPTIONS;

  if (removeSectionHeadlines) {
    filteredOptions = filteredOptions.filter((option) =>
      option.tool ? !SECTION_HEADLINE_TOOLS_TO_EXCLUDE.includes(option.tool) : true
    );
  }
  if (withFreeStyle) {
    filteredOptions = compact([
      { subheader: 'General writing' },
      { tool: GENERATION_TOOL.freestyle },
      projectType && isLongFormProjectType(projectType)
        ? { tool: GENERATION_TOOL.imageByStyle }
        : null,
      ...filteredOptions,
    ]);
  }

  // filter tools based on project type
  if (projectType) {
    // filter tools for "long-form" projects
    if (isLongFormProjectType(projectType)) {
      filteredOptions = filteredOptions.reduce((result: SectionToolOption[], option) => {
        if (option.tool && isMixtureTool(option.tool)) {
          let subTools = getToolSubToolOptions(option.tool);

          // Filter out incompatible subTools if the generation tool is blank
          if (generationTool && isBlankLongFormGenerationTool(generationTool)) {
            subTools = subTools.filter(
              (subTool) =>
                subTool.tool && !TOOLS_INCOMPATIBLE_WITH_BLANK_TEMPLATE.includes(subTool.tool)
            );
          }

          return [...result, ...subTools];
        }

        return [...result, option];
      }, []);
    }

    // filter tools for "short-form" projects
    if (isDocumentProjectType(projectType)) {
      filteredOptions = filteredOptions.reduce((result: SectionToolOption[], option) => {
        if (option.tool && isMixtureTool(option.tool)) {
          const subTools = getToolSubToolOptions(option.tool);

          return [...result, ...subTools];
        }

        return [...result, option];
      }, []);
    }

    // Source: Template-Builder - If template should include image we need to include the image tool.
    // Use the platform-specific image tool when the parent tool has a known pairing; otherwise generic.
    if (templateOutputType && doesTemplateOutputIncludeImages(templateOutputType)) {
      const specificImageTool = generationTool && getToolSpecificImageTool(generationTool);
      filteredOptions = [
        ...filteredOptions,
        { tool: specificImageTool ?? GENERATION_TOOL.imageByStyle },
      ];
    }

    // ... filter tools for "format" popup or "infinity" extension
  } else {
    filteredOptions = filteredOptions.reduce((result: SectionToolOption[], option) => {
      if (option.tool && isMixtureTool(option.tool)) {
        let subTools = getToolSubToolOptions(option.tool);

        if (targetType === 'format_popup') {
          subTools = subTools.filter(
            (subTool) =>
              subTool.tool && !TOOLS_INCOMPATIBLE_WITH_BLANK_TEMPLATE.includes(subTool.tool)
          );
        }

        return [...result, ...subTools];
      }

      return [...result, option];
    }, []);

    if (targetType === 'infinity') {
      // replace "freestyle" with "basic content improver" for infinity, because it better works with "content improver" requests
      filteredOptions = filteredOptions.map((option) => {
        if (option.tool && isFreestyleGenerationTool(option.tool)) {
          return {
            ...option,
            tool: GENERATION_TOOL.basicContentImprover,
          };
        }
        return option;
      });
    }
  }

  if (withoutSubHeaders) {
    filteredOptions = filteredOptions.filter((option) => option.tool);
  } else {
    filteredOptions = removeEmptySubHeaders(filteredOptions);
  }

  return uniqBy(filteredOptions, ({ tool, subheader }) => tool || subheader);
};

/**
 * Prepare "tool list" object for backend
 * @param tools - tools
 * @returns - tool list object
 */
export const prepareToolListForBackend = (tools: GenerationTool[]): Record<string, string> => {
  return tools.reduce((toolList: Record<string, string>, tool) => {
    const title = getToolTitle(tool);
    const description = getToolDescription(tool);
    toolList[tool] = `${title}${description ? ` - ${description}` : ''}`;
    return toolList;
  }, {});
};

/**
 * Get available generation tools object for the backend.
 * @param options - Options for filtering generation tools.
 * @param [options.projectType] - Whether the project is a long-form project.
 * @param [options.generationTool] - A specific generation tool to filter by.
 * @returns - The prepared tool list object.
 */
export const getAvailableToolsForBackend = ({
  projectType,
  generationTool,
  templateOutputType,
}: {
  projectType: ProjectType;
  generationTool?: GenerationTool;
  templateOutputType?: TemplateOutputType;
}): Record<string, string> => {
  const filteredToolItems = getSectionToolsOptions({
    projectType,
    generationTool,
    templateOutputType,
    withoutSubHeaders: true,
    removeSectionHeadlines: true,
  });

  // take only "tool" values
  let tools = filteredToolItems
    .map((toolItem) => toolItem.tool)
    .filter(Boolean) as GenerationTool[];

  if (isDocumentProjectType(projectType)) {
    tools.push(GENERATION_TOOL.googlePerformanceMaxAd);
  }

  if (generationTool && isFreestyleGenerationTool(generationTool)) {
    tools = tools.filter((tool) => !TOOLS_TO_REMOVE_FOR_FREESTYLE.includes(tool));

    tools.push(...TOOLS_TO_ADD_FOR_FREESTYLE);
  }

  return prepareToolListForBackend(tools);
};

/**
 * Retrieves sub-tool options for a given tool.
 * @param tool - The tool for which to get sub-tools.
 * @returns - Array of sub-tool options.
 */
const getToolSubToolOptions = (tool: GenerationTool): SectionToolOption[] => {
  /** Need to remove the below from selectors:
   * 1. Email - Cold email (defined in emailMixture -> classifiedMixtureSubGenerationTools)
   * 2. Email - Promotional email (defined in emailMixture -> classifiedMixtureSubGenerationTools)
   * @see https://www.notion.so/anyword/SFCI-single-asset-content-types-minor-changes-3019e8d84afb800cb865d042791f3743
   */
  if (tool === GENERATION_TOOL.emailMixture) {
    return [];
  }

  const subTools = getToolMixtureSubGenerationTools(tool);

  return subTools.map((tool) => ({
    tool,
  }));
};

const removeEmptySubHeaders = (options: SectionToolOption[]): SectionToolOption[] => {
  return options.filter((option, index, items) => {
    const nextOption = items[index + 1];

    // Remove current option if it is a subheader and the next option is also a subheader
    return !(option.subheader && nextOption?.subheader);
  });
};
